import React from 'react';
import { Box, Typography, Paper, Chip } from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { ComponentErrorBoundary } from './components/ComponentErrorBoundary';
import { useCANvisionContext } from './context/useCANvisionContext';
import EventsTab from './components/events/EventsTab';

// ---------------------------------------------------------------------------
// CANvisionEventsPage
// ---------------------------------------------------------------------------

/**
 * Standalone Events page at `/canvision/events`.
 *
 * Renders a full-page view of the Events tab content (EventsControlBar +
 * EventsTable + EventDetail drawer) without the dashboard chrome (no
 * attention feed, no stats cards, no site selector).
 *
 * This page lives inside the `<CANvisionLayout>` route tree, which means
 * it is already wrapped in `<CANvisionProvider>` and shares the same
 * WebSocket connection and context as the dashboard.
 *
 * Use case: operators can pop out the events view into a separate browser
 * tab for dedicated monitoring alongside the main dashboard.
 *
 * Created as part of Phase E, task MVP E-1 (Standalone Events Page).
 * Spec reference: docs/vision-dashboard/14-product-strategy-mvp.md section 11.
 */
export function CANvisionEventsPage() {
  const { socketConnection } = useCANvisionContext();
  const isConnected = socketConnection?.isConnected ?? false;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: { xs: 1, sm: 0 },
      }}
    >
      {/* Page header */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EventNoteIcon sx={{ fontSize: 28, color: 'primary.main' }} />
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            Events
          </Typography>
          <Chip
            size="small"
            icon={isConnected ? <WifiIcon /> : <WifiOffIcon />}
            label={isConnected ? 'Live' : 'Connecting...'}
            color={isConnected ? 'success' : 'default'}
            variant="outlined"
            sx={{ ml: 0.5 }}
            title={
              isConnected
                ? 'Real-time updates active'
                : 'Reconnecting to server...'
            }
          />
        </Box>
      </Paper>

      {/* Events tab content (control bar + table + detail drawer) */}
      <ComponentErrorBoundary sectionName="Events">
        <EventsTab />
      </ComponentErrorBoundary>
    </Box>
  );
}

export default CANvisionEventsPage;
